import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const friendSlice = createSlice({
    name: 'friend',
    initialState: {
        // All network lists
        projectNetworkList: [],      // friends
        encourageReceived: [],        // received requests
        encourageSent: [],            // sent requests
        declinePolitely: [],          // politely declined
        declineHarshly: [],           // harshly declined
        availableFreshNetworks: [],   // fresh/unknown networks
        
        // Performance optimization
        friendStatuses: {},           // O(1) lookup: { userId: 'status' }
        loading: {},                  // Per-user loading states
        globalLoading: false,         // For initial data load
        dataLoaded: false,
        error: null,
    },
    reducers: {
        // ✅ Set all network lists at once (INITIAL LOAD)
        setAllNetworkLists: (state, action) => {
            const { 
                projectNetworkList, 
                encourageReceived, 
                encourageSent,
                declinePolitely,
                declineHarshly,
                availableFreshNetworks 
            } = action.payload;
            
            state.projectNetworkList = projectNetworkList || [];
            state.encourageReceived = encourageReceived || [];
            state.encourageSent = encourageSent || [];
            state.declinePolitely = declinePolitely || [];
            state.declineHarshly = declineHarshly || [];
            state.availableFreshNetworks = availableFreshNetworks || [];
            state.dataLoaded = true;
            state.globalLoading = false;
            
            // ✅ Build O(1) lookup cache
            const statuses = {};
            projectNetworkList?.forEach(n => statuses[n._id] = 'friends');
            encourageReceived?.forEach(n => statuses[n._id] = 'received');
            encourageSent?.forEach(n => statuses[n._id] = 'sent');
            declinePolitely?.forEach(n => statuses[n._id] = 'declined-politely');
            declineHarshly?.forEach(n => statuses[n._id] = 'declined-harshly');
            availableFreshNetworks?.forEach(n => statuses[n._id] = 'fresh');
            
            state.friendStatuses = statuses;
        },
        
        // ✅ OPTIMIZED: Move network between lists (SINGLE OPERATION)
        moveNetworkBetweenLists: (state, action) => {
            const { networkId, networkData, fromStatus, toStatus } = action.payload;
            
            // Remove from old list
            switch (fromStatus) {
                case 'friends':
                    state.projectNetworkList = state.projectNetworkList.filter(n => n._id !== networkId);
                    break;
                case 'received':
                    state.encourageReceived = state.encourageReceived.filter(n => n._id !== networkId);
                    break;
                case 'sent':
                    state.encourageSent = state.encourageSent.filter(n => n._id !== networkId);
                    break;
                case 'declined-politely':
                    state.declinePolitely = state.declinePolitely.filter(n => n._id !== networkId);
                    break;
                case 'declined-harshly':
                    state.declineHarshly = state.declineHarshly.filter(n => n._id !== networkId);
                    break;
                case 'fresh':
                    state.availableFreshNetworks = state.availableFreshNetworks.filter(n => n._id !== networkId);
                    break;
            }
            
            // Add to new list (with basic data to prevent duplicates)
            const basicData = networkData || { _id: networkId };
            
            switch (toStatus) {
                case 'friends':
                    if (!state.projectNetworkList.some(n => n._id === networkId)) {
                        state.projectNetworkList.push(basicData);
                    }
                    break;
                case 'received':
                    if (!state.encourageReceived.some(n => n._id === networkId)) {
                        state.encourageReceived.push(basicData);
                    }
                    break;
                case 'sent':
                    if (!state.encourageSent.some(n => n._id === networkId)) {
                        state.encourageSent.push(basicData);
                    }
                    break;
                case 'declined-politely':
                    if (!state.declinePolitely.some(n => n._id === networkId)) {
                        state.declinePolitely.push(basicData);
                    }
                    break;
                case 'declined-harshly':
                    if (!state.declineHarshly.some(n => n._id === networkId)) {
                        state.declineHarshly.push(basicData);
                    }
                    break;
                case 'fresh':
                    if (!state.availableFreshNetworks.some(n => n._id === networkId)) {
                        state.availableFreshNetworks.push(basicData);
                    }
                    break;
            }
            
            // Update cache
            state.friendStatuses[networkId] = toStatus;
        },
        
        // ✅ Batch update multiple statuses (for bulk operations)
        batchUpdateStatuses: (state, action) => {
            const updates = action.payload; // Array of { networkId, fromStatus, toStatus }
            
            updates.forEach(({ networkId, fromStatus, toStatus, networkData }) => {
                friendSlice.caseReducers.moveNetworkBetweenLists(state, {
                    payload: { networkId, networkData, fromStatus, toStatus }
                });
            });
        },
        
        setLoading: (state, action) => {
            const { userId, isLoading } = action.payload;
            state.loading[userId] = isLoading;
        },
        
        setGlobalLoading: (state, action) => {
            state.globalLoading = action.payload;
        },
        
        setError: (state, action) => {
            state.error = action.payload;
        },
        
        clearError: (state) => {
            state.error = null;
        },
    },
});

export const { 
    setAllNetworkLists,
    moveNetworkBetweenLists,
    batchUpdateStatuses,
    setLoading,
    setGlobalLoading,
    setError,
    clearError
} = friendSlice.actions;

// ============================================
// THUNKS (ASYNC ACTIONS)
// ============================================

// ✅ INITIAL LOAD - Call once on app start
export const loadAllNetworks = () => async (dispatch, getState) => {
    // Prevent duplicate loads
    if (getState().friend.dataLoaded) {
        return { success: true, cached: true };
    }
    
    try {
        dispatch(setGlobalLoading(true));
        const token = localStorage.getItem('token');
        
        if (!token) {
            throw new Error('No authentication token');
        }
        
        const response = await axios.get('/api/pn/all-networks', {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        dispatch(setAllNetworkLists(response.data.pnLists));
        
        return { success: true };
    } catch (error) {
        // console.error('Networks load error:', error);
        dispatch(setGlobalLoading(false));
        dispatch(setError(error.response?.data?.message || error.message));
        return { success: false, error: error.message };
    }
};

// ✅ SEND FRIEND REQUEST
export const sendFriendRequest = (networkId, networkData) => async (dispatch, getState) => {
    const currentStatus = getState().friend.friendStatuses[networkId] || 'fresh';
    
    try {
        dispatch(setLoading({ userId: networkId, isLoading: true }));
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
            '/api/pn/encouraged',
            { networkId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Move from current list to 'sent' list
        dispatch(moveNetworkBetweenLists({
            networkId,
            networkData,
            fromStatus: currentStatus,
            toStatus: 'sent'
        }));
        
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        
        return { success: true, message: response.data.message || 'Friend request sent!' };
    } catch (error) {
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        return { success: false, error: error.response?.data?.message || error.message };
    }
};

// ✅ ACCEPT FRIEND REQUEST
export const acceptFriendRequest = (networkId, networkData) => async (dispatch, getState) => {
    const currentStatus = getState().friend.friendStatuses[networkId] || 'received';
    
    try {
        dispatch(setLoading({ userId: networkId, isLoading: true }));
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
            '/api/pn/encourage-support',
            { networkId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Move from 'received' to 'friends'
        dispatch(moveNetworkBetweenLists({
            networkId,
            networkData,
            fromStatus: currentStatus,
            toStatus: 'friends'
        }));
        
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        
        return { success: true, message: response.data.message || 'Friend request accepted!' };
    } catch (error) {
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        return { success: false, error: error.response?.data?.message || error.message };
    }
};

// ✅ DECLINE POLITELY
export const declineFriendRequestPolitely = (networkId) => async (dispatch, getState) => {
    const currentStatus = getState().friend.friendStatuses[networkId] || 'received';
    
    try {
        dispatch(setLoading({ userId: networkId, isLoading: true }));
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
            '/api/pn/discouraged',
            { networkId, type: 'polite' },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        dispatch(moveNetworkBetweenLists({
            networkId,
            fromStatus: currentStatus,
            toStatus: 'declined-politely'
        }));
        
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        
        return { success: true, message: response.data.message || 'Request declined politely' };
    } catch (error) {
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        return { success: false, error: error.response?.data?.message || error.message };
    }
};

// ✅ DECLINE HARSHLY
export const declineFriendRequestHarshly = (networkId) => async (dispatch, getState) => {
    const currentStatus = getState().friend.friendStatuses[networkId] || 'received';
    
    try {
        dispatch(setLoading({ userId: networkId, isLoading: true }));
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
            '/api/pn/discouraged',
            { networkId, type: 'harsh' },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        dispatch(moveNetworkBetweenLists({
            networkId,
            fromStatus: currentStatus,
            toStatus: 'declined-harshly'
        }));
        
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        
        return { success: true, message: response.data.message || 'Request declined harshly' };
    } catch (error) {
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        return { success: false, error: error.response?.data?.message || error.message };
    }
};

// ✅ REMOVE FRIEND
export const removeFriend = (networkId) => async (dispatch, getState) => {
    const currentStatus = getState().friend.friendStatuses[networkId] || 'friends';
    
    try {
        dispatch(setLoading({ userId: networkId, isLoading: true }));
        const token = localStorage.getItem('token');
        
        const response = await axios.post(
            '/api/pn/remove-network',
            { networkId },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        dispatch(moveNetworkBetweenLists({
            networkId,
            fromStatus: currentStatus,
            toStatus: 'fresh'
        }));
        
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        
        return { success: true, message: response.data.message || 'Friend removed!' };
    } catch (error) {
        dispatch(setLoading({ userId: networkId, isLoading: false }));
        return { success: false, error: error.response?.data?.message || error.message };
    }
};

// ============================================
// SELECTORS (MEMOIZED FOR PERFORMANCE)
// ============================================

// ✅ O(1) Status lookup
export const selectNetworkStatus = (state, networkId) => {
    return state.friend.friendStatuses[networkId] || 'unknown';
};

// ✅ Check if data is loaded
export const selectIsDataLoaded = (state) => state.friend.dataLoaded;

// ✅ Get loading state for specific user
export const selectUserLoading = (state, networkId) => {
    return state.friend.loading[networkId] || false;
};

// ✅ Get all friends
export const selectAllFriends = (state) => state.friend.projectNetworkList;

// ✅ Get pending requests count
export const selectPendingRequestsCount = (state) => state.friend.encourageReceived.length;

// ✅ Get all received requests
export const selectReceivedRequests = (state) => state.friend.encourageReceived;

// ✅ Get sent requests
export const selectSentRequests = (state) => state.friend.encourageSent;

// ✅ Check if user is friend
export const selectIsFriend = (state, networkId) => {
    return state.friend.friendStatuses[networkId] === 'friends';
};

// ✅ Get global loading state
export const selectGlobalLoading = (state) => state.friend.globalLoading;

export default friendSlice.reducer;